import { useQuery } from "@apollo/client";
import { useMemo } from "react";
import {
  ComplianceProgramStep,
  GetTopicTemplatesQuery,
  GetTopicTemplatesQueryVariables,
} from "types/graphql-schema";
import { FormError } from "types/topicflow";

import getTemplatesQuery from "@apps/templates/graphql/get-templates-query";
import ComboboxGeneric from "@components/combobox/generic-combobox";
import FormFields from "@components/form/form-fields";
import Loading from "@components/loading/loading";
import { assertEdgesNonNull } from "@helpers/helpers";

import { getMaxStepDueDateOffset, getMinStepDueDateOffset } from "../helpers";
import ProgramStepDueDate from "../steps/program-step-due-date";
import { ProgramForm, ProgramFormStep } from "../types";
import ToggleCard from "./toggle-card";

const OneOnOneToggleCard = ({
  canUpdate,
  proposedComplianceProgram,
  onToggleStep,
  onAddOrUpdateStep,
  onUpdateStepDueDateOffset,
  errors,
}: {
  canUpdate: boolean;
  proposedComplianceProgram: ProgramForm;
  onToggleStep: (enabled: boolean) => void;
  onAddOrUpdateStep: (step: ProgramFormStep) => void;
  onUpdateStepDueDateOffset: (offset: string | null) => void;
  errors: FormError[];
}) => {
  const topicTemplateError = errors.find(
    (error) => error.field === "topicTemplateId",
  )?.message;
  const { data, loading: isLoadingAvailableTopicTemplates } = useQuery<
    GetTopicTemplatesQuery,
    GetTopicTemplatesQueryVariables
  >(getTemplatesQuery);

  const availableTopicTemplates = useMemo(
    () =>
      data?.topicTemplates
        ? assertEdgesNonNull(data.topicTemplates).map((template) => ({
            label: template.title,
            value: template.id,
          }))
        : [],
    [data],
  );

  const oneononeStep = useMemo(
    () =>
      proposedComplianceProgram.steps.find(
        (step) => step.stepType === ComplianceProgramStep.OneOnOne,
      ),
    [proposedComplianceProgram.steps],
  );

  const selectedTopicTemplateId = useMemo(() => {
    return oneononeStep ? [oneononeStep.topicTemplateId] : [];
  }, [oneononeStep]);

  const isOneOneOneEnabled = proposedComplianceProgram.steps
    .map(({ stepType }) => stepType)
    .includes(ComplianceProgramStep.OneOnOne);

  return (
    <ToggleCard
      title="1:1 Meeting"
      enabled={isOneOneOneEnabled}
      disabled={!canUpdate}
      settings={
        isOneOneOneEnabled &&
        oneononeStep && (
          <FormFields
            fields={[
              {
                title: "Due Date",
                render: () => (
                  <ProgramStepDueDate
                    ongoingProgram={!!proposedComplianceProgram.ongoing}
                    programStartDate={proposedComplianceProgram.startDate}
                    minStepDueDateOffset={getMinStepDueDateOffset(
                      proposedComplianceProgram,
                      ComplianceProgramStep.OneOnOne,
                    )}
                    maxStepDueDateOffset={getMaxStepDueDateOffset(
                      proposedComplianceProgram,
                      ComplianceProgramStep.OneOnOne,
                    )}
                    stepDueDateOffset={
                      proposedComplianceProgram.steps.find(
                        (s) => s.stepType === ComplianceProgramStep.OneOnOne,
                      )?.dueDateOffset
                    }
                    onUpdate={(offset) => onUpdateStepDueDateOffset(offset)}
                  />
                ),
              },
              {
                title: "Meeting template",
                id: "field-topicTemplateId",
                error: topicTemplateError,
                render: () =>
                  isLoadingAvailableTopicTemplates ? (
                    <Loading mini />
                  ) : (
                    <ComboboxGeneric
                      options={availableTopicTemplates}
                      disabled={!canUpdate}
                      onChangeValue={(opt) =>
                        onAddOrUpdateStep({
                          ...oneononeStep,
                          topicTemplateId: opt.value,
                        })
                      }
                      value={
                        selectedTopicTemplateId.length > 0
                          ? (availableTopicTemplates.find(
                              ({ value }) =>
                                value === selectedTopicTemplateId[0],
                            ) ?? null)
                          : null
                      }
                    />
                  ),
              },
            ]}
          />
        )
      }
      onChange={onToggleStep}
    />
  );
};

export default OneOnOneToggleCard;
